// Multi-Domain Analytics System for Google Analytics 4 (delivery via GTM)

// TypeScript declarations
declare global {
  interface Window {
    gtag: (command: string, ...args: any[]) => void;
    dataLayer: any[];
  }
}

// Domain-to-tracking-ID mappings
export const analyticsConfig = {
  'bidmio.com': {
    gaId: import.meta.env.VITE_GA_ID_EN || '',
  },
  'bidmio.sk': {
    gaId: import.meta.env.VITE_GA_ID_SK || '',
  },
  'bidmio.cz': {
    gaId: import.meta.env.VITE_GA_ID_CZ || '',
  },
  'bidmio.dk': {
    gaId: import.meta.env.VITE_GA_ID_DK || '',
  },
  'bidmio.de': {
    gaId: import.meta.env.VITE_GA_ID_DE || '',
  },
};

export type DomainKey = keyof typeof analyticsConfig;

// Get configuration for current domain
export const getDomainConfig = () => {
  if (typeof window === 'undefined') return null;
  
  // www.bidmio.de is the canonical German host, so match without the www prefix.
  const hostname = window.location.hostname.replace(/^www\./, '');
  
  // Check for exact match first
  if (hostname in analyticsConfig) {
    return analyticsConfig[hostname as DomainKey];
  }
  
  // Fallback for development/staging environments (localhost, replit.dev, etc.)
  // Use language detection to determine which tracking to use
  const pathLang = window.location.pathname.split('/')[1];
  const domainMap: Record<string, DomainKey> = {
    sk: 'bidmio.sk',
    cs: 'bidmio.cz',
    da: 'bidmio.dk',
    en: 'bidmio.com',
    de: 'bidmio.de',
  };
  
  const fallbackDomain = domainMap[pathLang] || 'bidmio.com';
  return analyticsConfig[fallbackDomain];
};

// ============================================================================
// GOOGLE ANALYTICS 4 FUNCTIONS
// ============================================================================

export const initGA = () => {
  if (typeof window === 'undefined') return;
  
  const config = getDomainConfig();
  if (!config?.gaId) {
    // Silently skip GA4 initialization if no ID is configured
    return;
  }

  // Load GA4 script if not already loaded
  if (!document.querySelector(`script[src*="googletagmanager.com/gtag/js"]`)) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${config.gaId}`;
    document.head.appendChild(script);
  }

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
  
  window.gtag('js', new Date());
  window.gtag('config', config.gaId, {
    send_page_view: true,
    cookie_flags: 'SameSite=None;Secure',
  });

  console.log('GA4 initialized for', window.location.hostname, 'with ID:', config.gaId);
};

export const trackPageView = (url?: string, title?: string) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  
  const config = getDomainConfig();
  if (!config?.gaId) return;

  window.gtag('config', config.gaId, {
    page_path: url || window.location.pathname,
    page_title: title || document.title,
  });
};

export const trackEvent = (
  action: string,
  category?: string,
  label?: string,
  value?: number,
  params?: Record<string, any>
) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  
  window.gtag('event', action, {
    event_category: category,
    event_label: label,
    value: value,
    ...params,
  });
};

export const trackConversion = (event: string, data?: Record<string, any>) => {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('event', event, {
    event_category: 'conversion',
    ...data,
  });
};

// ============================================================================
// CONVENIENCE TRACKING HELPERS (GA4)
// ============================================================================

export const trackButtonClick = (buttonName: string, location: string) => {
  trackEvent('button_click', 'engagement', `${buttonName} - ${location}`);
};

export const trackFormSubmit = (formName: string) => {
  trackEvent('form_submit', 'conversion', formName);
};

export const trackDemoRequest = () => {
  trackEvent('demo_request', 'conversion', 'demo_form');
};

export const trackCTAClick = (ctaText: string, ctaLocation: string) => {
  trackEvent('cta_click', 'engagement', `${ctaText} - ${ctaLocation}`);
};

export const trackPricingInteraction = (planName: string, action: string) => {
  trackEvent(action, 'pricing', planName);
};

export const trackModuleInteraction = (moduleName: string, action: string) => {
  trackEvent(action, 'module', moduleName);
};

export const trackContact = (method: string) => {
  trackEvent('contact', 'conversion', method);
};

export const trackSignup = () => {
  trackConversion('sign_up');
};

// Map UI language code to BCP-47 locale.
const LANG_LOCALE: Record<string, string> = {
  sk: 'sk-SK',
  cs: 'cs-CZ',
  da: 'da-DK',
  en: 'en-US',
  de: 'de-DE',
};
const toLocale = (lang: string): string => LANG_LOCALE[lang] || lang;

// Fired when the user submits the demo-access modal form.
export const trackDemoRequestSubmitted = (source: string, lang: string) => {
  trackEvent('demo_request_submitted', 'conversion', source, undefined, {
    source,
    lang,
    locale: toLocale(lang),
    transport_type: 'beacon',
  });
};

// Fired by the server-rendered interstitial after the magic-link token is validated.
// Kept here so any client surface (e.g. modal-opened side-effect or future emails)
// can dispatch with the same event contract.
export const trackDemoLinkClicked = (source: string, lang: string) => {
  trackEvent('demo_link_clicked', 'conversion', source, undefined, {
    source,
    lang,
    locale: toLocale(lang),
    transport_type: 'beacon',
  });
};

// Fired by the server-rendered interstitial right before the user is redirected
// to demo.bidmio.app — i.e. when demo access is actually granted.
export const trackDemoGranted = (source: string, lang: string) => {
  trackEvent('demo_granted', 'conversion', source, undefined, {
    source,
    lang,
    locale: toLocale(lang),
    transport_type: 'beacon',
  });
};

// Backwards-compat alias used by older call sites; mapped to the current
// "link clicked" event so existing dashboards keep receiving signal.
export const trackDemoClick = (source: string, lang: string, _ref?: string) => {
  trackDemoLinkClicked(source, lang);
};

/** Marketing "Book a call" CTA → country `/book/demo` (meeting-booking B06). */
export const trackBookDemoClicked = (source: string, lang: string) => {
  trackEvent("book_demo_clicked", "conversion", source, undefined, {
    source,
    lang,
    locale: toLocale(lang),
  });
};
